import {hidePrivateGalleries} from "@/domain/galleryTree/visibility";

const state = {
    checkinId: null,
    rootGalleryId: null,
    gallery: null,
    mediaDict: {}, // Dictionary to store media by galleryId+tab key
    priceList: null
}

export const getters = {
    checkinId: state => state.checkinId,
    rootGalleryId: state => state.rootGalleryId,
    gallery: state => state.gallery,
    media: state => (galleryId, tab) => {
        const key = `${galleryId}_${tab}`
        return state.mediaDict[key] || null
    },
    priceList: state => state.priceList,
    priceListStructure: (state) => ({items}) => {
        if (!items || !items.length) return false;
        let prices = items.filter(item => item.unit_price || item.price);
        // if is range price list
        if (prices.some(item => item.from_qty && item.to_qty)) {
            prices = prices.map(item => ({
                key: `${item.from_qty}-${item.to_qty}`,
                from_qty: item.from_qty,
                to_qty: item.to_qty,
                price: item.unit_price,
                price_original: item.unit_price,
            }));
        } else {
            prices = prices.map(item => ({
                key: item.qty,
                qty: item.qty,
                price: item.price || Math.round(item.unit_price * item.qty * 10) / 10,
                price_original: item.price_original || Math.round(item.unit_price * item.qty * 10) / 10
            }));
        }
        if (prices.length > 0) prices[prices.length - 1].last = true;
        return prices;
    }
}

export const mutations = {
    setCheckinId(state, checkinId) {
        const isDifferent = state.checkinId !== checkinId
        state.checkinId = checkinId
        if (isDifferent) {
            state.rootGalleryId = null
            state.gallery = null
            state.mediaDict = {} // Reset all media when checkinId changes
            state.priceList = null
        }
    },
    setRootGalleryId(state, galleryId) {
        const isDifferent = state.rootGalleryId !== galleryId
        state.rootGalleryId = galleryId
        state.gallery = null
        // Reset all media when rootGalleryId changes
        if (isDifferent) {
            state.mediaDict = {}
        }
        state.priceList = null
    },
    setGallery(state, gallery) {
        state.gallery = gallery
    },
    setMedia(state, {galleryId, tab, media, page, append = false, paginationType = 'pagination'}) {
        const key = `${galleryId}_${tab}`
        const existingData = state.mediaDict[key] || {data: [], isDirty: false}

        if (append && existingData.data) {
            // Append new items to existing data
            state.mediaDict = {
                ...state.mediaDict,
                [key]: {
                    data: [...existingData.data, ...media],
                    currentPage: page || existingData.currentPage || 1,
                    isDirty: false,
                    paginationType: paginationType,
                    hasBeenFetched: true
                }
            }
        } else {
            // Replace existing data
            state.mediaDict = {
                ...state.mediaDict,
                [key]: {
                    data: media,
                    currentPage: page || 1,
                    isDirty: false,
                    paginationType: paginationType,
                    hasBeenFetched: true
                }
            }
        }
    },
    setPriceList(state, priceList) {
        state.priceList = priceList
    },
    updateFavoriteCount(state, {galleryId, delta}) {
        if (!state.gallery || state.gallery.id !== galleryId) {
            return
        }

        // Find the favourite tab and update its count
        const favoriteTab = state.gallery.tabs.find(tab => tab.type === 'favourite')
        if (!favoriteTab) {
            return
        }

        favoriteTab.count = Math.max(0, favoriteTab.count + delta)
    },
    invalidateFavoritesCache(state, galleryId) {
        // Mark the favorites tab data as dirty instead of deleting
        const key = `${galleryId}_favourite`
        if (!state.mediaDict[key]) {
            return
        }

        state.mediaDict = {
            ...state.mediaDict,
            [key]: {
                ...state.mediaDict[key],
                isDirty: true
            }
        }
    },
    markMediaAsDirty(state, {galleryId, tab}) {
        const key = `${galleryId}_${tab}`
        if (!state.mediaDict[key]) {
            return
        }

        state.mediaDict = {
            ...state.mediaDict,
            [key]: {
                ...state.mediaDict[key],
                isDirty: true
            }
        }
    },
    updateMediaFavoriteStatus(state, {galleryId, mediaId, isFavorite}) {
        // Update favorite status in all tabs of the specific gallery
        Object.keys(state.mediaDict).forEach(key => {
            if (!key.startsWith(`${galleryId}_`)) {
                return
            }

            const mediaData = state.mediaDict[key]
            if (!mediaData || !mediaData.data) {
                return
            }

            const mediaIndex = mediaData.data.findIndex(m => m.id === mediaId)
            if (mediaIndex === -1) {
                return
            }

            // Create new array and object to ensure Vue reactivity
            const updatedData = [...mediaData.data]
            updatedData[mediaIndex] = {
                ...updatedData[mediaIndex],
                favorite: isFavorite
            }
            state.mediaDict = {
                ...state.mediaDict,
                [key]: {
                    ...mediaData,
                    data: updatedData
                }
            }
        })
    },
    clearGalleryTree(state) {
        //state.checkinId = null
        state.rootGalleryId = null
        state.gallery = null
        state.mediaDict = {}
        state.priceList = null
    }
}

export const actions = service => ({
    setCheckinId({state, commit, dispatch}, checkinId) {
        if (state.checkinId === checkinId) return false
        commit('setCheckinId', checkinId)
        // As per requirements, setCheckinId should call setRootGalleryId with null
        dispatch('setRootGalleryId', null)
        return true
    },
    setRootGalleryId({state, commit}, galleryId) {
        if (galleryId === null) {
            commit('setRootGalleryId', null)
            return true
        }
        if (galleryId <= 0) galleryId = 0
        if (state.rootGalleryId === galleryId) return false
        commit('setRootGalleryId', galleryId)
        return true
    },
    async fetchGallery({state, commit, rootGetters}) {
        const gallery = await service.getGalleryTree(state.checkinId, state.rootGalleryId)
        commit('setGallery', hidePrivateGalleries(gallery, rootGetters['checkinDetail/canSeeMedia']))
    },
    async fetchMedia({state, commit}, {
        galleryId = null,
        tab,
        page = 1,
        params = {},
        append = false,
        paginationType = 'pagination'
    } = {}) {
        // Use the current rootGalleryId if none is provided
        const targetGalleryId = galleryId !== null ? galleryId : state.rootGalleryId
        const key = `${targetGalleryId}_${tab}`
        const existingData = state.mediaDict[key]

        // Check if we need to fetch
        let shouldFetch = false

        if (!existingData || !existingData.hasBeenFetched || existingData.isDirty) {
            // Data needs to be fetched
            shouldFetch = true
        } else if (!append && paginationType !== 'infinite' && page === 1 && existingData.currentPage > 1) {
            // For non-infinite scroll, refetch when going to page 1 from a different page
            shouldFetch = true
        } else if (page !== existingData.currentPage) {
            // Different page requested
            shouldFetch = true
        } else if (append) {
            // Appending data (infinite scroll)
            shouldFetch = true
        }

        if (!shouldFetch) {
            return existingData.data
        }

        // Fetch media for the specified gallery and tab
        const media = await service.getGalleryMedia(state.checkinId, targetGalleryId, {...params, tab, page})

        // Store the media with the galleryId, tab and page information
        commit('setMedia', {galleryId: targetGalleryId, tab, media, page, append, paginationType})

        return media
    },
    async fetchPriceList({state, commit}, params = {}) {
        commit('setPriceList', await service.getPriceList(state.checkinId, state.rootGalleryId, params))
    },
    async addToFavourites({commit}, {mediaId, galleryId}) {
        try {
            await service.addToFavourites(mediaId)

            // Update favorite count
            commit('updateFavoriteCount', {galleryId, delta: 1})
            // Invalidate favorites cache
            commit('invalidateFavoritesCache', galleryId)
            // Update the media item's favorite status in all tabs of this gallery
            commit('updateMediaFavoriteStatus', {galleryId, mediaId, isFavorite: true})

            return true
        } catch (error) {
            return false
        }
    },
    async removeFromFavourites({commit}, {mediaId, galleryId}) {
        try {
            await service.removeFromFavourites(mediaId)

            // Update favorite count
            commit('updateFavoriteCount', {galleryId, delta: -1})
            // Invalidate favorites cache
            commit('invalidateFavoritesCache', galleryId)
            // Update the media item's favorite status in all tabs of this gallery
            commit('updateMediaFavoriteStatus', {galleryId, mediaId, isFavorite: false})

            return true
        } catch (error) {
            return false
        }
    },
    async checkNewMedia({state, rootGetters}) {
        try {
            const freshGallery = hidePrivateGalleries(
                await service.getGalleryTree(state.checkinId, state.rootGalleryId),
                rootGetters['checkinDetail/canSeeMedia']
            )
            const currentGallery = state.gallery

            if (!currentGallery || !freshGallery) {
                return {hasNewMedia: false, totalNewMedia: 0, changedGalleries: []}
            }

            const changedGalleries = []
            let totalNewMedia = 0

            // Check main gallery media count
            if (freshGallery.num_media > currentGallery.num_media) {
                const newCount = freshGallery.num_media - currentGallery.num_media
                changedGalleries.push({
                    id: freshGallery.id,
                    name: freshGallery.name,
                    newCount,
                    type: 'main'
                })
                totalNewMedia += newCount
            }

            // Check subgalleries media counts
            if (freshGallery.sub_gallery && currentGallery.sub_gallery) {
                freshGallery.sub_gallery.forEach(freshSub => {
                    const currentSub = currentGallery.sub_gallery.find(s => s.id === freshSub.id)
                    if (currentSub && freshSub.num_media > currentSub.num_media) {
                        const newCount = freshSub.num_media - currentSub.num_media
                        changedGalleries.push({
                            id: freshSub.id,
                            name: freshSub.name,
                            newCount,
                            type: 'sub'
                        })
                        totalNewMedia += newCount
                    }
                })
            }

            // Check for new subgalleries that now have media
            if (freshGallery.sub_gallery) {
                freshGallery.sub_gallery.forEach(freshSub => {
                    const currentSub = currentGallery.sub_gallery ? currentGallery.sub_gallery.find(s => s.id === freshSub.id) : undefined
                    if (!currentSub && freshSub.num_media > 0) {
                        changedGalleries.push({
                            id: freshSub.id,
                            name: freshSub.name,
                            newCount: freshSub.num_media,
                            type: 'new_sub'
                        })
                        totalNewMedia += freshSub.num_media
                    }
                })
            }

            return {
                hasNewMedia: totalNewMedia > 0,
                totalNewMedia,
                changedGalleries,
                freshData: freshGallery
            }
        } catch (error) {
            console.error('Error checking new media in galleryTree:', error)
            return {hasNewMedia: false, totalNewMedia: 0, changedGalleries: []}
        }
    },
    clearGalleryTree({commit}) {
        commit('clearGalleryTree')
    }
})

export default service => ({
    namespaced: true,
    state,
    getters,
    mutations,
    actions: actions(service)
})
